import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Button,
  Group,
  Select,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { mapUserRole } from '@utils/mappers';

import {
  GetUsersRequestParams,
  Roles,
  User,
  UsersSortBy,
} from '@interfaces/users.interface';

import { isApiError } from '@api/index';
import {
  useCountUsersQuery,
  useDeleteUserMutation,
  useGetManyUsersQuery,
  useUpdateUserMutation,
} from '@api/users.api';

import useAuth from '@hooks/useAuth';
import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertUserForm, {
  UPSERT_USER_FORM_MODAL_ID,
} from '@components/users/UpsertUserForm';

export default function UsersList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user } = useAuth();

  const canCreateUsers = useUserPermissionGuard('USERS.CREATE');
  const canEditUsers = useUserPermissionGuard('USERS.UPDATE');
  const canDeleteUsers = useUserPermissionGuard('USERS.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetUsersRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'name',
    sortOrder: 'asc',
    role: undefined,
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<User>>({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as UsersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: usersCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountUsersQuery({
      role: filters.role,
      nameSearch: nameSearchDebounced,
      emailSearch: emailSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyUsersQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const [updateUser, { isLoading: isLoadingUpdateUser }] =
    useUpdateUserMutation();
  const [deleteUser, { isLoading: isLoadingDeleteUser }] =
    useDeleteUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddUserClick = useCallback(() => {
    openModal({
      modalId: UPSERT_USER_FORM_MODAL_ID,
      title: 'Registra utente amministratore',
      children: <UpsertUserForm />,
    });
  }, []);

  const handleEditUserClick = useCallback((user: User) => {
    openModal({
      modalId: UPSERT_USER_FORM_MODAL_ID,
      title: 'Modifica utente',
      children: <UpsertUserForm user={user} />,
    });
  }, []);

  const handleDeleteUserClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina utente',
      children:
        "Stai per eliminare l'utente. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteUser(id).unwrap();

          showNotification({
            title: 'Utente eliminato',
            message: 'Utente eliminato con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconUsers />
        Utenti {!isLoadingCount ? `(${usersCount.count})` : ''}
      </Group>
    ),
    [usersCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateUsers ? (
        <Button leftSection={<IconPlus />} onClick={handleAddUserClick}>
          Registra amministratore
        </Button>
      ) : (
        <></>
      ),
    [handleAddUserClick, canCreateUsers],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessun utente trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'active',
            title: 'Attivo',
            width: '0%',
            render: (record) => (
              <Tooltip
                label={
                  !canEditUsers
                    ? "Non hai i permessi per modificare l'utente"
                    : record.id === user?.id
                      ? 'Non puoi disabilitare il tuo account'
                      : record.activationTokenCreatedAt
                        ? "L'utente non ha ancora attivato il suo account"
                        : record.active
                          ? 'Disabilita'
                          : 'Abilita'
                }
              >
                <div>
                  <Switch
                    disabled={
                      !canEditUsers ||
                      record.id === user?.id ||
                      record.activationTokenCreatedAt !== null ||
                      isLoadingUpdateUser
                    }
                    checked={record.active}
                    onClick={
                      record.active
                        ? () =>
                            updateUser({
                              id: record.id,
                              body: { active: false },
                            })
                        : () =>
                            updateUser({
                              id: record.id,
                              body: { active: true },
                            })
                    }
                  />
                </div>
              </Tooltip>
            ),
          },
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'email',
            title: 'Email',
            filter: (
              <TextInput
                placeholder="Ricerca email"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  emailSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setEmailSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'role',
            title: 'Ruolo',
            render: (record) => mapUserRole(record.role),
            filter: (
              <Select
                placeholder="Filtra ruolo"
                data={[
                  {
                    value: 'admin',
                    label: mapUserRole('admin'),
                  },
                  {
                    value: 'medicalOrganization',
                    label: mapUserRole('medicalOrganization'),
                  },
                  {
                    value: 'medicalDoctor',
                    label: mapUserRole('medicalDoctor'),
                  },
                ]}
                value={filters.role}
                onChange={(value) =>
                  setFilters((f) => ({
                    ...f,
                    role: (value as Roles) ?? undefined,
                  }))
                }
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {record.id !== user?.id && (
                  <>
                    {canEditUsers && (
                      <Tooltip label="Modifica utente">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => handleEditUserClick(record)}
                        >
                          <IconPencil />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {canDeleteUsers && (
                      <Tooltip label="Elimina utente">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={isLoadingDeleteUser}
                          onClick={() => handleDeleteUserClick(record.id)}
                        >
                          <IconTrash />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={usersCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
